
import { API_BASE } from '../utils/apiConfig';

let tokenInMemory = null;
let userInMemory = null;
let isInitialized = false;

export async function login(username, password) {
  try {
    console.log('🔐 Iniciando processo de login...');

    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Falha no login");
    }

    // Salvar token e usuário na memória e storage
    tokenInMemory = data.token;
    userInMemory = data.user;

    try {
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("user", JSON.stringify(data.user));
    } catch (storageError) {
      console.warn('Erro ao salvar no storage:', storageError);
    }

    console.log('✅ Login realizado com sucesso');
    return data;
  } catch (error) {
    console.error('❌ Erro no login:', error);
    throw error;
  }
}

export async function me() {
  const token = getToken();
  if (!token) {
    throw new Error("Token não encontrado");
  }

  try {
    const response = await fetch(`${API_BASE}/api/auth/me`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
    });

    if (response.status === 401) {
      console.warn('🔒 Sessão expirada');
      clearAuthData();
      throw new Error("Sessão expirada");
    }

    if (!response.ok) {
      throw new Error(`Erro na validação: ${response.status}`);
    }

    const userData = await response.json();
    userInMemory = userData;

    // Atualizar storage também
    try {
      sessionStorage.setItem("user", JSON.stringify(userData));
    } catch (storageError) {
      console.warn('Erro ao atualizar storage:', storageError);
    }

    return userData;
  } catch (error) {
    if (error.message.includes("fetch") || error.name === "TypeError") {
      console.warn('⚠️ Erro de conexão ao validar usuário');
    }
    throw error;
  }
}

export async function changePassword(oldPassword, newPassword) {
  const token = getToken();
  if (!token) {
    throw new Error("Token não encontrado");
  }

  try {
    console.log('🔑 Alterando senha...');

    const response = await fetch(`${API_BASE}/api/auth/change-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        oldPassword: oldPassword.trim(),
        newPassword: newPassword.trim()
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || "Erro ao alterar senha");
    }

    console.log('✅ Senha alterada com sucesso');
    return data;
  } catch (error) {
    console.error('❌ Erro ao alterar senha:', error);
    throw error;
  }
}

function clearAuthData() {
  console.log('🧹 Limpando dados de autenticação...');

  // Limpar memória
  tokenInMemory = null;
  userInMemory = null;

  // Limpar storage
  try {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  } catch (e) {
    console.warn('⚠️ Erro ao limpar storage:', e);
  }
}

export function signOut() {
  console.log('🚪 Executando logout...');

  try {
    // Limpar dados
    clearAuthData();

    // Limpar variáveis globais
    tokenInMemory = null;
    userInMemory = null;

    console.log('🔄 Redirecionando...');

    // Redirecionar imediatamente
    window.location.replace(window.location.origin + window.location.pathname);

  } catch (error) {
    console.error('❌ Erro durante logout:', error);
    // Fallback
    window.location.reload();
  }
}

export function getToken() {
  return tokenInMemory || sessionStorage.getItem("token") || localStorage.getItem("token");
}

export function getUser() {
  if (userInMemory) {
    return userInMemory;
  }

  try {
    const sessionUser = sessionStorage.getItem("user");
    const localUser = localStorage.getItem("user");
    const userData = sessionUser || localUser;

    if (userData) {
      const parsedUser = JSON.parse(userData);
      userInMemory = parsedUser; // Cache na memória
      return parsedUser;
    }
  } catch (e) {
    console.warn('⚠️ Erro ao recuperar dados do usuário:', e);
  }

  return null;
}

export function isAuthenticated() {
  const token = getToken();
  const user = getUser();
  return !!(token && user);
}

export function isAdmin() {
  const user = getUser();
  return user && (user.is_admin === true || user.role === 'admin');
}

export function canAccessStrategicMap() {
  const user = getUser();
  // Admin sempre pode acessar
  if (user && (user.is_admin === true || user.role === 'admin')) {
    return true;
  }
  // Verificar permissões específicas do usuário (implementar conforme necessário)
  return user && user.permissions && user.permissions.strategic_map === true;
}

export function canAccessScorecard() {
  const user = getUser();
  // Admin sempre pode acessar
  if (user && (user.is_admin === true || user.role === 'admin')) {
    return true;
  }
  // Verificar permissões específicas do usuário (implementar conforme necessário)
  return user && user.permissions && user.permissions.scorecard === true;
}

export function canAccessIndicatorValues() {
  const user = getUser();
  // Admin sempre pode acessar
  if (user && (user.is_admin === true || user.role === 'admin')) {
    return true;
  }
  // Verificar permissões específicas do usuário (implementar conforme necessário)
  return user && user.permissions && user.permissions.indicator_values === true;
}

export async function authenticatedFetch(url, options = {}) {
  const token = getToken();

  console.log('🔄 Fazendo requisição autenticada para:', url);

  const config = {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  try {
    const response = await fetch(url, config);

    if (response.status === 401 || response.status === 403) {
      console.error('🔒 Erro de autenticação, fazendo logout');
      signOut();
      throw new Error("Sessão expirada ou sem permissão");
    }

    return response;
  } catch (error) {
    if (error.message.includes("Failed to fetch") || error.name === "TypeError") {
      console.warn('⚠️ Erro de conexão com a API:', error.message);
      throw new Error("Erro de conexão - API offline");
    }

    throw error;
  }
}

export function initAuth() {
  if (isInitialized) {
    return;
  }

  isInitialized = true;
  console.log('🔧 Inicializando autenticação...');

  const token = sessionStorage.getItem("token") || localStorage.getItem("token");
  const userData = sessionStorage.getItem("user") || localStorage.getItem("user");

  if (token && userData) {
    try {
      tokenInMemory = token;
      userInMemory = JSON.parse(userData);
      console.log('✅ Dados de autenticação carregados da sessão');

      // Validar token em background
      me().catch((error) => {
        console.warn('⚠️ Token inválido, limpando dados:', error.message);
        clearAuthData();
      });
    } catch (e) {
      console.warn('⚠️ Erro ao carregar dados da sessão:', e);
      clearAuthData();
    }
  }
}

// Auto-inicializar quando o módulo for carregado
initAuth();
